import { denyNappletControllerAccess, originRegistry, type ShellAdapter, type ShellBridge } from '@kehto/shell';
import { NAPPLET_KIND_SNAPSHOT } from '@kehto/nip/5d';

import {
  PAJA_DEV_SIGNER_PUBKEY,
  type PajaShellAdapter,
} from './browser-adapter.js';
import {
  appendPajaMessageLog,
  renderPajaDevtools,
  type PajaDevtoolsState,
} from './browser-devtools.js';
import { isPajaLocalTarget, type PajaRuntimeTarget } from './local-target.js';
import type { PajaHostConfig } from './options.js';
import type { PajaResolvedPointer } from './runtime-resolver.js';
import type { PajaSimulation } from './simulation.js';

type PajaRuntimeStatus = 'booting' | 'ready' | 'reloading' | 'error';

export const PAJA_RUNTIME_TABS_STORAGE_KEY = 'kehto:paja:runtime-tabs:v1';

export interface PajaRuntimeTabsSnapshot {
  readonly version: 1;
  readonly pointers: readonly string[];
  readonly activeIndex: number;
}

export interface PajaRuntimeTabsSnapshotState {
  readonly activeTabId: string | null;
  readonly tabs: readonly Pick<PajaRuntimeTab, 'id' | 'pointerValue'>[];
}

export interface PajaRuntimeTab {
  id: string;
  title: string;
  key: string;
  pointerValue: string;
  pointerStatus: string;
  resolvedTarget: PajaRuntimeTarget;
  frame: HTMLIFrameElement;
  generation: number;
  windowId: string | null;
  status: PajaRuntimeStatus;
}

export interface PajaRuntimeTabState extends PajaDevtoolsState {
  pointerValue: string;
  pointerStatus: string;
  tabs: PajaRuntimeTab[];
  activeTabId: string | null;
  generation: number;
  status: PajaRuntimeStatus;
  activateTab(tabId: string): void;
  closeTab(tabId: string): void;
  /** Open the host-owned intent-link builder for an advertised verified tab. */
  createIntentLink?(tab: PajaRuntimeTab): void;
}

export interface PajaRuntimeTabRuntime {
  currentSimulation: PajaSimulation;
  currentWindowId: string | null;
  readyWindowIds: Set<string>;
}

export interface PajaRuntimeTabContext {
  config: PajaHostConfig;
  stage: HTMLElement;
  bridge: ShellBridge;
  adapter: PajaShellAdapter;
  runtime: PajaRuntimeTabRuntime;
  navigateFrame(
    frame: HTMLIFrameElement,
    config: PajaHostConfig,
    generation: number,
    adapter: ShellAdapter,
    resolvedTarget: PajaRuntimeTarget,
    windowId: string,
    isCurrent?: () => boolean,
    onRegistered?: (windowId: string | null) => void,
  ): Promise<string | null>;
  renderTargetErrorHtml(error: unknown): string;
  setPointerStatus(state: PajaRuntimeTabState, message: string): void;
  setStatus(state: PajaRuntimeTabState, status: PajaRuntimeStatus): void;
  /** Clear host-owned retained-delivery state before session and frame teardown. */
  onTabDestroyed?(tab: PajaRuntimeTab): void;
}

export type PajaDuplicateChoice = 'load-again' | 'open-tab' | 'cancel';

/**
 * Return the duplicate-detection key for a runtime target.
 *
 * @param target - Verified pointer or local development file.
 * @returns Snapshot keys include the signed event ID; replaceable pointer keys include kind, author, d-tag and artifact hash. Local keys include d-tag and hash.
 *
 * @example
 * ```ts
 * state.tabs.find((tab) => tab.key === resolvedTargetKey(target));
 * ```
 */
export function resolvedTargetKey(target: PajaRuntimeTarget): string {
  if (isPajaLocalTarget(target)) return ['local', target.dTag, target.aggregateHash].join(':');
  if (target.event.kind === NAPPLET_KIND_SNAPSHOT) {
    return [target.event.kind, target.event.id, target.aggregateHash].join(':');
  }
  return [
    target.event.kind,
    target.event.pubkey,
    target.dTag,
    target.aggregateHash,
  ].join(':');
}

/** Return the opaque identity of one live runtime-tab generation. */
export function runtimeTabGenerationId(tab: Pick<PajaRuntimeTab, 'id' | 'generation'>): string {
  return `${tab.id}:${tab.generation}`;
}

/**
 * Bind verified pointer servers to the exact runtime-tab window generation.
 * Local development files carry no server hints, so they keep the host defaults.
 */
export function bindRuntimeTabBlossomServers(
  adapter: Pick<PajaShellAdapter, 'setWindowBlossomServers'>,
  windowId: string,
  resolvedTarget: Pick<PajaResolvedPointer, 'blossomServers'> | PajaRuntimeTarget,
): void {
  if (isPajaLocalTarget(resolvedTarget)) return;
  adapter.setWindowBlossomServers(windowId, resolvedTarget.blossomServers);
}

export function getActiveTab(state: PajaRuntimeTabState): PajaRuntimeTab | null {
  return state.tabs.find((tab) => tab.id === state.activeTabId) ?? null;
}

export function setEmptyStageVisible(visible: boolean): void {
  const empty = document.getElementById('empty-runtime-stage');
  if (empty instanceof HTMLElement) empty.hidden = !visible;
}

export function createPajaShareUrl(pointerValue: string, href = window.location.href): string {
  const pointer = pointerValue.trim();
  const url = new URL(href);
  url.search = '';
  url.hash = '';
  url.searchParams.set(getPointerParamName(pointer), pointer);
  return url.href;
}

export function snapshotRuntimeTabs(state: PajaRuntimeTabsSnapshotState): PajaRuntimeTabsSnapshot | null {
  // Local-file tabs have no pointer and cannot be re-read after a reload, so
  // only pointer tabs persist and the active index counts pointer tabs only.
  const persisted = state.tabs.filter((tab) => tab.pointerValue.trim().length > 0);
  const pointers = persisted.map((tab) => tab.pointerValue.trim());
  if (pointers.length === 0) return null;
  const activeIndex = Math.max(
    0,
    persisted.findIndex((tab) => tab.id === state.activeTabId),
  );
  return {
    version: 1,
    pointers,
    activeIndex: Math.min(activeIndex, pointers.length - 1),
  };
}

export function parseRuntimeTabsSnapshot(value: string | null): PajaRuntimeTabsSnapshot | null {
  if (!value) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const candidate = parsed as {
    readonly version?: unknown;
    readonly pointers?: unknown;
    readonly activeIndex?: unknown;
  };
  if (candidate.version !== 1 || !Array.isArray(candidate.pointers)) return null;
  const pointers = candidate.pointers
    .filter((pointer): pointer is string => typeof pointer === 'string')
    .map((pointer) => pointer.trim())
    .filter((pointer) => pointer.length > 0);
  if (pointers.length === 0) return null;
  const rawActiveIndex = typeof candidate.activeIndex === 'number' && Number.isInteger(candidate.activeIndex)
    ? candidate.activeIndex
    : 0;
  return {
    version: 1,
    pointers,
    activeIndex: Math.max(0, Math.min(rawActiveIndex, pointers.length - 1)),
  };
}

export function renderRuntimeTabs(state: PajaRuntimeTabState): void {
  document.title = getActiveTab(state)?.title || '@kehto/paja';
  const tabsEl = document.getElementById('napplet-tabs');
  if (!(tabsEl instanceof HTMLElement)) return;
  tabsEl.replaceChildren(...state.tabs.map((tab) => renderTab(state, tab)));
}

export function activateRuntimeTab(
  state: PajaRuntimeTabState,
  context: PajaRuntimeTabContext,
  tabId: string,
): void {
  const tab = state.tabs.find((entry) => entry.id === tabId);
  if (!tab) return;
  state.activeTabId = tab.id;
  state.resolvedTarget = tab.resolvedTarget;
  state.pointerValue = tab.pointerValue;
  state.pointerStatus = tab.pointerStatus;
  context.runtime.currentWindowId = tab.windowId;

  const input = document.getElementById('runtime-pointer-input');
  if (input instanceof HTMLInputElement) input.value = tab.pointerValue;
  context.setPointerStatus(state, tab.pointerStatus);
  context.setStatus(state, tab.status);
  setEmptyStageVisible(false);
  for (const entry of state.tabs) entry.frame.hidden = entry.id !== tab.id;
  renderRuntimeTabs(state);
  renderPajaDevtools(state, { bridge: context.bridge, devSignerPubkey: PAJA_DEV_SIGNER_PUBKEY });
}

export function closeRuntimeTab(
  state: PajaRuntimeTabState,
  context: PajaRuntimeTabContext,
  tabId: string,
): void {
  const index = state.tabs.findIndex((tab) => tab.id === tabId);
  if (index < 0) return;
  const [tab] = state.tabs.splice(index, 1);
  destroyRuntimeTab(tab, context);
  if (state.activeTabId !== tabId) {
    renderRuntimeTabs(state);
    setEmptyStageVisible(state.tabs.length === 0);
    return;
  }
  const next = state.tabs[Math.min(index, state.tabs.length - 1)] ?? null;
  if (next) {
    activateRuntimeTab(state, context, next.id);
    return;
  }
  state.activeTabId = null;
  state.resolvedTarget = null;
  context.runtime.currentWindowId = null;
  state.pointerStatus = 'idle';
  context.setPointerStatus(state, 'idle');
  context.setStatus(state, 'ready');
  setEmptyStageVisible(true);
  renderRuntimeTabs(state);
}

export function addRuntimeTab(
  state: PajaRuntimeTabState,
  context: PajaRuntimeTabContext,
  pointerValue: string,
  resolvedTarget: PajaRuntimeTarget,
): PajaRuntimeTab {
  state.generation += 1;
  const tab: PajaRuntimeTab = {
    id: `tab-${state.generation}`,
    title: resolvedTargetTitle(resolvedTarget),
    key: resolvedTargetKey(resolvedTarget),
    pointerValue,
    pointerStatus: `${resolvedTarget.dTag}:${resolvedTarget.aggregateHash.slice(0, 12)}`,
    resolvedTarget,
    frame: document.createElement('iframe'),
    generation: state.generation,
    windowId: null,
    status: 'booting',
  };
  tab.frame = createRuntimeTabFrame(tab, state, context);
  context.stage.append(tab.frame);
  state.tabs.push(tab);
  activateRuntimeTab(state, context, tab.id);
  startRuntimeTabNavigation(tab, state, context);
  return tab;
}

export function reloadActiveRuntimeTab(
  state: PajaRuntimeTabState,
  context: PajaRuntimeTabContext,
): void {
  const tab = getActiveTab(state);
  if (!tab) return;
  destroyRuntimeTab(tab, context);
  tab.generation = ++state.generation;
  tab.windowId = null;
  tab.status = 'reloading';
  tab.frame = createRuntimeTabFrame(tab, state, context);
  context.stage.append(tab.frame);
  activateRuntimeTab(state, context, tab.id);
  startRuntimeTabNavigation(tab, state, context);
}

export function showDuplicatePointerDialog(): Promise<PajaDuplicateChoice> {
  const backdrop = document.getElementById('duplicate-pointer-dialog');
  const loadAgain = document.getElementById('duplicate-load-again');
  const openTab = document.getElementById('duplicate-open-tab');
  const cancel = document.getElementById('duplicate-cancel');
  if (
    !(backdrop instanceof HTMLElement)
    || !(loadAgain instanceof HTMLButtonElement)
    || !(openTab instanceof HTMLButtonElement)
    || !(cancel instanceof HTMLButtonElement)
  ) {
    return Promise.resolve('cancel');
  }

  return new Promise((resolve) => {
    const cleanup = (choice: PajaDuplicateChoice) => {
      backdrop.hidden = true;
      loadAgain.removeEventListener('click', onLoadAgain);
      openTab.removeEventListener('click', onOpenTab);
      cancel.removeEventListener('click', onCancel);
      backdrop.removeEventListener('keydown', onKeydown);
      resolve(choice);
    };
    const onLoadAgain = () => cleanup('load-again');
    const onOpenTab = () => cleanup('open-tab');
    const onCancel = () => cleanup('cancel');
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cleanup('cancel');
    };
    loadAgain.addEventListener('click', onLoadAgain);
    openTab.addEventListener('click', onOpenTab);
    cancel.addEventListener('click', onCancel);
    backdrop.addEventListener('keydown', onKeydown);
    backdrop.hidden = false;
    loadAgain.focus();
  });
}

function renderTab(state: PajaRuntimeTabState, tab: PajaRuntimeTab): HTMLElement {
  const tabButton = document.createElement('div');
  tabButton.className = 'tab';
  tabButton.dataset.tabId = tab.id;
  tabButton.dataset.active = String(tab.id === state.activeTabId);
  tabButton.dataset.source = isPajaLocalTarget(tab.resolvedTarget) ? 'local' : 'pointer';
  tabButton.setAttribute('role', 'tab');
  tabButton.setAttribute('aria-selected', String(tab.id === state.activeTabId));
  tabButton.tabIndex = 0;
  tabButton.title = tab.pointerValue || tab.title;
  tabButton.addEventListener('click', () => state.activateTab(tab.id));
  tabButton.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    state.activateTab(tab.id);
  });

  const label = document.createElement('span');
  label.className = 'tab-label';
  label.textContent = tab.title;
  if (isPajaLocalTarget(tab.resolvedTarget)) tabButton.append(label, renderCloseButton(state, tab));
  else {
    tabButton.append(label, renderShareButton(state, tab));
    tabButton.append(renderCloseButton(state, tab));
  }
  return tabButton;
}

/**
 * Report whether the native Share action can offer intent-link creation.
 *
 * @param state - Tab state containing the optional host builder callback.
 * @param tab - Runtime tab whose target was verified before it was opened.
 * @returns Whether the tab is a verified advertised pointer and the host can create a link.
 * @example
 * ```ts
 * if (canCreatePajaIntentLink(state, tab)) openShareDialog(state, tab, button);
 * ```
 */
export function canCreatePajaIntentLink(
  state: Pick<PajaRuntimeTabState, 'createIntentLink'>,
  tab: Pick<PajaRuntimeTab, 'resolvedTarget'>,
): boolean {
  return !isPajaLocalTarget(tab.resolvedTarget)
    && tab.resolvedTarget.manifest.archetypes.length > 0
    && state.createIntentLink !== undefined;
}

function renderShareButton(state: PajaRuntimeTabState, tab: PajaRuntimeTab): HTMLButtonElement {
  const share = document.createElement('button');
  share.type = 'button';
  share.className = 'tab-share';
  share.setAttribute('aria-label', `Share ${tab.title}`);
  share.title = 'Share';
  share.textContent = '↗';
  share.addEventListener('click', (event) => {
    event.stopPropagation();
    openShareDialog(state, tab, share);
  });
  share.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    openShareDialog(state, tab, share);
  });
  return share;
}

function openShareDialog(state: PajaRuntimeTabState, tab: PajaRuntimeTab, opener: HTMLButtonElement): void {
  const dialog = document.createElement('div');
  dialog.className = 'dialog-backdrop';
  dialog.id = 'paja-share-dialog';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  const panel = document.createElement('div');
  panel.className = 'dialog';
  const title = document.createElement('div');
  title.id = 'paja-share-dialog-title';
  title.className = 'dialog-title';
  title.textContent = 'Share';
  dialog.setAttribute('aria-labelledby', title.id);
  const description = document.createElement('div');
  description.className = 'dialog-copy';
  const offersIntents = canCreatePajaIntentLink(state, tab);
  description.textContent = offersIntents
    ? `Share ${tab.title} as an app link or create an intent link.` : `Share ${tab.title} as an app link.`;
  const linkLabel = document.createElement('label');
  linkLabel.className = 'config-field';
  linkLabel.textContent = 'App link';
  const link = document.createElement('input');
  link.type = 'text';
  link.readOnly = true;
  link.value = createPajaShareUrl(tab.pointerValue);
  linkLabel.append(link);
  const status = document.createElement('div');
  status.setAttribute('role', 'status');
  const actions = document.createElement('div');
  actions.className = 'dialog-actions';
  const copy = document.createElement('button');
  copy.type = 'button';
  copy.textContent = 'Copy app link';
  const create = document.createElement('button');
  create.type = 'button';
  create.textContent = 'Create intent link';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.textContent = 'Cancel';
  actions.append(copy, ...(offersIntents ? [create] : []), cancel);
  panel.append(title, description, linkLabel, status, actions);
  dialog.append(panel);
  // Use the host overlay layer: native top-layer dialogs can route input to an
  // opaque iframe underneath them despite showModal() making that frame inert.
  const siblings = Array.from(document.body.children)
    .filter((node): node is HTMLElement => node instanceof HTMLElement)
    .map((node) => ({ node, inert: node.inert }));
  for (const { node } of siblings) node.inert = true;
  document.body.append(dialog);
  const retainModalFocus = (event: FocusEvent) => {
    if (!dialog.isConnected) return;
    const next = event.relatedTarget;
    if (next instanceof Node && dialog.contains(next)) return;
    queueMicrotask(() => {
      if (dialog.isConnected && !dialog.matches(':focus-within')) copy.focus();
    });
  };
  const close = (restoreFocus = true) => {
    dialog.removeEventListener('focusout', retainModalFocus);
    dialog.remove();
    for (const { node, inert } of siblings) node.inert = inert;
    if (restoreFocus) opener.focus();
  };
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const controls = [link, copy, ...(offersIntents ? [create] : []), cancel];
    const next = event.shiftKey ? controls.at(-1)! : controls[0]!;
    const edge = event.shiftKey ? controls[0] : controls.at(-1);
    if (document.activeElement === edge) { event.preventDefault(); next.focus(); }
  });
  cancel.addEventListener('click', () => close());
  copy.addEventListener('click', () => { void copyShareLink(link, status); });
  create.addEventListener('click', () => {
    close();
    state.createIntentLink?.(tab);
  });
  dialog.addEventListener('focusout', retainModalFocus);
  copy.focus();
}

function renderCloseButton(state: PajaRuntimeTabState, tab: PajaRuntimeTab): HTMLButtonElement {
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'tab-close';
  close.setAttribute('aria-label', `Close ${tab.title}`);
  close.textContent = 'x';
  close.addEventListener('click', (event) => {
    event.stopPropagation();
    state.closeTab(tab.id);
  });
  close.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    state.closeTab(tab.id);
  });
  return close;
}

async function copyShareLink(link: HTMLInputElement, status: HTMLElement): Promise<void> {
  try {
    await navigator.clipboard.writeText(link.value);
    status.textContent = 'App link copied.';
  } catch {
    link.focus();
    link.select();
    status.textContent = 'Select and copy the app link.';
  }
}

function getPointerParamName(pointer: string): 'naddr' | 'nevent' | 'pointer' {
  if (pointer.startsWith('naddr')) return 'naddr';
  if (pointer.startsWith('nevent')) return 'nevent';
  return 'pointer';
}

/**
 * Return the tab title for a runtime target.
 *
 * @param target - Verified pointer or local development file.
 * @returns The local file name, else the manifest title, else the dTag or hash prefix.
 *
 * @example
 * ```ts
 * tab.title = resolvedTargetTitle(target);
 * ```
 */
export function resolvedTargetTitle(target: PajaRuntimeTarget): string {
  if (isPajaLocalTarget(target)) return target.fileName;
  const titleTag = target.event.tags.find((tag) => tag[0] === 'title')?.[1];
  const title = typeof target.manifest.title === 'string' && target.manifest.title.trim().length > 0
    ? target.manifest.title.trim()
    : titleTag?.trim();
  return title && title.length > 0 ? title : target.dTag || target.aggregateHash.slice(0, 12);
}

function destroyRuntimeTab(tab: PajaRuntimeTab, context: PajaRuntimeTabContext): void {
  context.onTabDestroyed?.(tab);
  if (tab.windowId) {
    context.bridge.runtime.destroyWindow(tab.windowId);
    context.bridge.runtime.sessionRegistry.unregister(tab.windowId);
    originRegistry.unregister(tab.windowId);
    context.runtime.readyWindowIds.delete(tab.windowId);
    if (context.runtime.currentWindowId === tab.windowId) context.runtime.currentWindowId = null;
  }
  tab.frame.remove();
}

function createRuntimeTabFrame(
  tab: PajaRuntimeTab,
  state: PajaRuntimeTabState,
  context: PajaRuntimeTabContext,
): HTMLIFrameElement {
  const frame = document.createElement('iframe');
  frame.id = `napplet-frame-${tab.id}`;
  frame.className = 'tab-panel';
  frame.title = `Napplet runtime target: ${tab.title}`;
  denyNappletControllerAccess(frame);
  frame.sandbox.add('allow-scripts');
  frame.sandbox.remove('allow-same-origin');
  frame.dataset.tabId = tab.id;
  frame.dataset.targetUrl = tab.pointerValue;
  frame.hidden = true;
  frame.addEventListener('error', () => {
    tab.status = 'error';
    if (state.activeTabId === tab.id) context.setStatus(state, 'error');
    renderRuntimeTabs(state);
  });
  return frame;
}

function startRuntimeTabNavigation(
  tab: PajaRuntimeTab,
  state: PajaRuntimeTabState,
  context: PajaRuntimeTabContext,
): void {
  const generation = tab.generation;
  tab.status = 'booting';
  context.setStatus(state, 'booting');
  renderRuntimeTabs(state);
  const windowId = runtimeTabWindowId(context.config, tab);
  bindRuntimeTabBlossomServers(context.adapter, windowId, tab.resolvedTarget);
  void context.navigateFrame(
    tab.frame,
    context.config,
    generation,
    context.adapter,
    tab.resolvedTarget,
    windowId,
    () => tab.generation === generation,
    (windowId) => {
      if (tab.generation !== generation) return;
      tab.windowId = windowId;
      if (state.activeTabId === tab.id) context.runtime.currentWindowId = windowId;
    },
  ).then((windowId) => {
    if (tab.generation !== generation) return;
    tab.windowId = windowId;
    if (state.activeTabId === tab.id) context.runtime.currentWindowId = windowId;
  }).catch((error) => {
    if (tab.generation !== generation) return;
    tab.frame.removeAttribute('src');
    tab.frame.srcdoc = context.renderTargetErrorHtml(error);
    tab.status = 'error';
    if (state.activeTabId === tab.id) context.setStatus(state, 'error');
    appendPajaMessageLog(state, 'paja', {
      type: 'paja.target.error',
      error: error instanceof Error ? error.message : String(error),
    }, tab.windowId ?? undefined);
    renderRuntimeTabs(state);
    console.error(error);
  });
}

function runtimeTabWindowId(config: PajaHostConfig, tab: PajaRuntimeTab): string {
  return `${config.window.id}:${tab.id}:${tab.generation}`;
}
